import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { API, useAuth } from "@/App";
import { asArray } from "@/lib/arrays";
import DashboardLayout from "@/components/layout/DashboardLayout";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Video, Calendar, Clock, Users, ExternalLink, Plus, X,
  Wifi, CheckCircle, Play, Link2, AlertTriangle, Settings,
  ShieldCheck, Key, HelpCircle, Loader2, Lock, Radio
} from "lucide-react";

const pad = (value) => String(value).padStart(2, "0");
const toLocalDateValue = (date) => `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
const defaultClassDate = () => {
  const date = new Date();
  date.setDate(date.getDate() + 1);
  return toLocalDateValue(date);
};
const dateOptions = Array.from({ length: 45 }, (_, index) => {
  const date = new Date();
  date.setDate(date.getDate() + index);
  return {
    value: toLocalDateValue(date),
    label: date.toLocaleDateString("en-IN", {
      weekday: "short",
      day: "numeric",
      month: "short",
      year: "numeric",
    }),
  };
});
const timeOptions = Array.from({ length: 56 }, (_, index) => {
  const totalMinutes = 6 * 60 + index * 15;
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  const value = `${pad(hours)}:${pad(minutes)}`;
  const label = new Date(2026, 0, 1, hours, minutes).toLocaleTimeString("en-IN", {
    hour: "2-digit",
    minute: "2-digit",
  });
  return { value, label };
});
const durationOptions = [15, 30, 45, 60, 75, 90, 120, 150, 180, 240];

const emptyClassForm = (defaultSource = "manual") => ({
  title: "",
  description: "",
  scheduled_date: defaultClassDate(),
  scheduled_time: "19:00",
  duration: 60,
  source: defaultSource,
  meeting_link: "",
  course_id: "",
});

const LiveClassesPage = () => {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const isMentor = user?.role === "mentor";
  const isManagement = user?.role === "management";
  const isStaff = isAdmin || isMentor || isManagement;
  const [classes, setClasses] = useState([]);
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [creating, setCreating] = useState(false);
  const [formError, setFormError] = useState("");
  const [form, setForm] = useState(() => emptyClassForm("manual"));

  // Zoom configuration state
  const [zoomStatus, setZoomStatus] = useState({ configured: false });
  const [showZoomSettings, setShowZoomSettings] = useState(false);
  const [zoomConfig, setZoomConfig] = useState({ account_id: "", client_id: "", client_secret: "", host_user_id: "me" });
  const [savingZoom, setSavingZoom] = useState(false);
  const [zoomMsg, setZoomMsg] = useState({ type: "", text: "" });

  const fetchClasses = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/live-classes`, { withCredentials: true });
      setClasses(asArray(res.data, "classes"));
    } catch (err) {
      console.error("Error fetching live classes:", err);
    } finally { setLoading(false); }
  }, []);

  const fetchZoomStatus = useCallback(async () => {
    if (!isStaff) return;
    try {
      const res = await axios.get(`${API}/live-classes/zoom-status`, { withCredentials: true });
      setZoomStatus(res.data || { configured: false });
      if (res.data?.configured) {
        setForm(f => ({ ...f, source: f.source || "zoom" }));
      }
    } catch (err) {
      console.error("Error fetching zoom status:", err);
    }
  }, [isStaff]);

  useEffect(() => {
    fetchClasses();
    fetchZoomStatus();
  }, [fetchClasses, fetchZoomStatus]);

  useEffect(() => {
    if (!isStaff) return;
    axios.get(`${API}/courses`)
      .then(res => setCourses(asArray(res.data, "courses")))
      .catch(err => console.error("Error fetching courses:", err));
  }, [isStaff]);

  const loadZoomConfig = async () => {
    if (!isAdmin) return;
    try {
      const res = await axios.get(`${API}/live-classes/zoom-config`, { withCredentials: true });
      if (res.data) {
        setZoomConfig({
          account_id: res.data.account_id || "",
          client_id: res.data.client_id || "",
          client_secret: "",
          host_user_id: res.data.host_user_id || "me"
        });
      }
    } catch (err) {
      console.error("Error loading zoom config:", err);
    }
  };

  const handleOpenZoomSettings = () => {
    setZoomMsg({ type: "", text: "" });
    loadZoomConfig();
    setShowZoomSettings(true);
  };

  const saveZoomConfig = async (e) => {
    e.preventDefault();
    setSavingZoom(true);
    setZoomMsg({ type: "", text: "" });
    try {
      const res = await axios.post(`${API}/live-classes/zoom-config`, zoomConfig, { withCredentials: true });
      setZoomMsg({ type: "success", text: res.data?.message || "Zoom connected successfully!" });
      fetchZoomStatus();
      setTimeout(() => {
        setShowZoomSettings(false);
      }, 1500);
    } catch (err) {
      const msg = err.response?.data?.detail || "Failed to connect to Zoom. Please check your credentials.";
      setZoomMsg({ type: "error", text: msg });
    } finally {
      setSavingZoom(false);
    }
  };

  const createClass = async (e) => {
    e.preventDefault();
    setFormError("");
    setCreating(true);

    if (form.source === "manual" && !form.meeting_link?.trim()) {
      setFormError("Please enter a meeting link (Zoom, Google Meet, etc.)");
      setCreating(false);
      return;
    }

    try {
      const scheduledAt = `${form.scheduled_date}T${form.scheduled_time}:00`;
      await axios.post(`${API}/live-classes`, {
        ...form,
        scheduled_at: scheduledAt,
        course_id: form.course_id || null
      }, { withCredentials: true });
      setShowCreate(false);
      setForm(emptyClassForm(zoomStatus.configured ? "zoom" : "manual"));
      fetchClasses();
    } catch (err) {
      const errorMsg = err.response?.data?.detail || "Failed to create class";
      setFormError(errorMsg);
    } finally {
      setCreating(false);
    }
  };

  const deleteClass = async (classId) => {
    if (!window.confirm("Delete this class?")) return;
    try {
      await axios.delete(`${API}/live-classes/${classId}`, { withCredentials: true });
      fetchClasses();
    } catch (err) { alert("Failed to delete"); }
  };

  const statusConfig = {
    upcoming: { color: "#00F0FF", bg: "rgba(0,240,255,0.1)", label: "Upcoming", icon: Calendar },
    live: { color: "#00FF88", bg: "rgba(0,255,136,0.1)", label: "LIVE NOW", icon: Wifi },
    completed: { color: "#94A3B8", bg: "rgba(148,163,184,0.1)", label: "Completed", icon: CheckCircle },
  };

  if (loading) return (
    <DashboardLayout title="Live Classes">
      <div className="flex items-center justify-center h-64">
        <div className="w-10 h-10 border-4 border-[#00FF88] border-t-transparent rounded-full animate-spin" />
      </div>
    </DashboardLayout>
  );

  return (
    <DashboardLayout title="Live Classes">
      <div className="space-y-5" data-testid="live-classes-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div>
            <h1 className="text-xl font-bold text-gray-900 dark:text-white font-outfit flex items-center gap-2">
              <Video className="w-5 h-5 text-[#00F0FF]" /> Live Classes
            </h1>
            <p className="text-xs text-gray-500 dark:text-[#94A3B8] font-plex mt-1">Join live trading sessions & workshops</p>
          </div>
          {isStaff && (
            <div className="flex items-center gap-2">
              {isAdmin && (
                <Button
                  onClick={handleOpenZoomSettings}
                  variant="outline"
                  size="sm"
                  className="h-9 px-3 text-xs font-semibold rounded-sm border-gray-300 dark:border-white/10 text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white hover:border-[#00F0FF] hover:bg-slate-100 dark:hover:bg-white/10"
                >
                  <Settings className="w-3.5 h-3.5 mr-1.5 text-[#00F0FF]" />
                  Zoom API {zoomStatus.configured ? <span className="ml-1.5 w-2 h-2 rounded-full bg-[#00FF88] inline-block" /> : <span className="ml-1.5 text-[10px] text-amber-400 font-mono">(Offline)</span>}
                </Button>
              )}
              <Button onClick={() => {
                setFormError("");
                setForm(emptyClassForm(zoomStatus.configured ? "zoom" : "manual"));
                setShowCreate(true);
              }}
                style={{ backgroundColor: "#00FF88", color: "#000" }}
                className="font-bold rounded-sm text-sm h-9 px-4 border-0" data-testid="create-class-btn">
                <Plus className="w-4 h-4 mr-1" /> Schedule Class
              </Button>
            </div>
          )}
        </div>

        {/* Create Class Modal */}
        {showCreate && (
          <div className="fixed inset-0 bg-black/75 backdrop-blur-sm z-50 flex items-center justify-center p-4" data-testid="create-class-modal">
            <div className="terminal-card rounded-xl p-6 w-full max-w-lg border border-gray-200 dark:border-white/10 shadow-2xl">
              <div className="flex items-center justify-between mb-5">
                <div>
                  <h2 className="text-gray-900 dark:text-white font-outfit font-bold text-lg">Schedule Live Class</h2>
                  <p className="text-xs text-gray-500 dark:text-[#94A3B8] font-plex">Create automated Zoom meeting or paste any live stream link</p>
                </div>
                <button onClick={() => setShowCreate(false)} className="text-gray-400 dark:text-[#52525B] hover:text-gray-900 dark:text-white p-1"><X className="w-5 h-5" /></button>
              </div>

              {/* In-Modal Error Notification */}
              {formError && (
                <div className="mb-4 p-3.5 rounded-lg bg-red-500/10 border border-red-500/30 text-xs text-red-400 space-y-2">
                  <div className="flex items-start gap-2">
                    <AlertTriangle className="w-4 h-4 text-red-400 shrink-0 mt-0.5" />
                    <div className="flex-1">
                      <p className="font-semibold text-red-300">{formError}</p>
                      {form.source === "zoom" && (
                        <p className="text-[11px] text-red-300/80 mt-1">
                          Tip: You can schedule right now without Zoom API by switching to <strong>Paste Link</strong> and entering your Zoom / Google Meet invite URL.
                        </p>
                      )}
                    </div>
                  </div>
                  {form.source === "zoom" && (
                    <div className="flex gap-2 pt-1 pl-6">
                      <Button
                        type="button"
                        size="sm"
                        onClick={() => {
                          setForm(f => ({ ...f, source: "manual" }));
                          setFormError("");
                        }}
                        className="h-7 text-xs bg-[#00FF88] text-black font-semibold hover:bg-[#00FF88]/90"
                      >
                        <Link2 className="w-3 h-3 mr-1" /> Switch to Paste Link
                      </Button>
                      {isAdmin && (
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          onClick={() => {
                            setShowCreate(false);
                            handleOpenZoomSettings();
                          }}
                          className="h-7 text-xs border-red-400/40 text-red-200 hover:bg-red-400/10"
                        >
                          <Settings className="w-3 h-3 mr-1" /> Configure Zoom API
                        </Button>
                      )}
                    </div>
                  )}
                </div>
              )}

              <form onSubmit={createClass} className="space-y-4">
                {/* Source Toggle */}
                <div className="flex items-center gap-2 p-1 bg-gray-100 dark:bg-white/[0.04] rounded-lg border border-gray-200 dark:border-white/[0.08]">
                  <Button type="button" size="sm" variant="ghost"
                    onClick={() => {
                      setForm(f => ({ ...f, source: "manual" }));
                      setFormError("");
                    }}
                    style={form.source === "manual" ? { backgroundColor: "#00F0FF", color: "#000" } : {}}
                    className={`flex-1 text-xs font-jetbrains h-8 px-3 rounded-md transition-all ${form.source === "manual" ? "font-bold shadow-sm" : "text-gray-500 dark:text-[#94A3B8]"}`}>
                    <Link2 className="w-3.5 h-3.5 mr-1.5" /> Paste Link (Direct)
                  </Button>
                  <Button type="button" size="sm" variant="ghost"
                    onClick={() => {
                      setForm(f => ({ ...f, source: "zoom" }));
                      setFormError("");
                    }}
                    style={form.source === "zoom" ? { backgroundColor: "#00F0FF", color: "#000" } : {}}
                    className={`flex-1 text-xs font-jetbrains h-8 px-3 rounded-md transition-all ${form.source === "zoom" ? "font-bold shadow-sm" : "text-gray-500 dark:text-[#94A3B8]"}`}>
                    <Video className="w-3.5 h-3.5 mr-1.5" />
                    Auto Zoom Meeting
                    {zoomStatus.configured ? (
                      <span className="ml-1.5 w-1.5 h-1.5 rounded-full bg-[#00FF88]" title="Zoom API Connected" />
                    ) : (
                      <span className="ml-1 text-[9px] px-1 py-0.2 rounded bg-amber-500/20 text-amber-400 border border-amber-500/30">Setup</span>
                    )}
                  </Button>
                </div>

                {/* If Auto Zoom is selected but credentials are not configured, guide the user */}
                {form.source === "zoom" && !zoomStatus.configured && (
                  <div className="p-3 rounded-lg bg-amber-500/10 border border-amber-500/20 text-xs text-amber-300 space-y-2">
                    <div className="flex items-center gap-2 font-medium">
                      <AlertTriangle className="w-4 h-4 text-amber-400 shrink-0" />
                      <span>Zoom Automated API not configured yet</span>
                    </div>
                    <p className="text-[11px] text-amber-300/80 leading-relaxed">
                      Instant option: Switch to <strong>"Paste Link (Direct)"</strong> and paste your Zoom / Google Meet meeting URL.
                    </p>
                    <div className="flex gap-2 pt-1">
                      <Button
                        type="button"
                        size="sm"
                        onClick={() => setForm(f => ({ ...f, source: "manual" }))}
                        className="h-7 text-xs bg-amber-400 text-black font-semibold hover:bg-amber-300"
                      >
                        <Link2 className="w-3 h-3 mr-1" /> Use Paste Link (Fastest)
                      </Button>
                      {isAdmin && (
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          onClick={() => {
                            setShowCreate(false);
                            handleOpenZoomSettings();
                          }}
                          className="h-7 text-xs border-amber-400/40 text-amber-300 hover:bg-amber-400/20"
                        >
                          <Settings className="w-3 h-3 mr-1" /> Connect Zoom API
                        </Button>
                      )}
                    </div>
                  </div>
                )}

                <div>
                  <Label className="text-gray-500 dark:text-[#94A3B8] text-xs font-medium">Session Title</Label>
                  <Input value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
                    placeholder="e.g., Live Market Analysis & Price Action Setup"
                    className="mt-1 bg-gray-50 dark:bg-white/[0.03] border-gray-200 dark:border-white/[0.08] text-gray-900 dark:text-white font-plex" required data-testid="class-title-input" />
                </div>

                <div>
                  <Label className="text-gray-500 dark:text-[#94A3B8] text-xs font-medium">Description</Label>
                  <textarea value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
                    placeholder="Key concepts, setups, and Q&A details to be covered..."
                    className="mt-1 w-full h-20 bg-gray-50 dark:bg-white/[0.03] border border-gray-200 dark:border-white/[0.08] rounded-md p-3 text-gray-900 dark:text-white text-sm font-plex placeholder:text-gray-400 dark:placeholder:text-[#52525B] focus:outline-none focus:border-[#00FF88]/50 resize-none" />
                </div>

                <div>
                  <Label className="text-gray-500 dark:text-[#94A3B8] text-xs font-medium">Audience</Label>
                  <select
                    value={form.course_id}
                    onChange={e => setForm(f => ({ ...f, course_id: e.target.value }))}
                    className="mt-1 w-full h-10 bg-gray-50 dark:bg-white/[0.03] border border-gray-200 dark:border-white/[0.08] rounded-md px-3 text-gray-900 dark:text-white text-sm font-plex focus:outline-none focus:border-[#00FF88]/50"
                    data-testid="class-course-select"
                  >
                    <option value="" className="bg-white dark:bg-[#0C0C10] text-gray-900 dark:text-white">All Students (Entire Academy)</option>
                    {courses.map(course => (
                      <option key={course.course_id} value={course.course_id} className="bg-white dark:bg-[#0C0C10] text-gray-900 dark:text-white">
                        {course.title}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <div>
                    <Label className="text-gray-500 dark:text-[#94A3B8] text-xs font-medium">Date</Label>
                    <select
                      value={form.scheduled_date}
                      onChange={e => setForm(f => ({ ...f, scheduled_date: e.target.value }))}
                      className="mt-1 w-full h-10 bg-gray-50 dark:bg-white/[0.03] border border-gray-200 dark:border-white/[0.08] rounded-md px-3 text-gray-900 dark:text-white text-sm font-plex focus:outline-none focus:border-[#00FF88]/50"
                      required
                      data-testid="class-date-select"
                    >
                      {dateOptions.map(option => (
                        <option key={option.value} value={option.value} className="bg-white dark:bg-[#0C0C10] text-gray-900 dark:text-white">
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <Label className="text-gray-500 dark:text-[#94A3B8] text-xs font-medium">Time</Label>
                    <select
                      value={form.scheduled_time}
                      onChange={e => setForm(f => ({ ...f, scheduled_time: e.target.value }))}
                      className="mt-1 w-full h-10 bg-gray-50 dark:bg-white/[0.03] border border-gray-200 dark:border-white/[0.08] rounded-md px-3 text-gray-900 dark:text-white text-sm font-plex focus:outline-none focus:border-[#00FF88]/50"
                      required
                      data-testid="class-time-select"
                    >
                      {timeOptions.map(option => (
                        <option key={option.value} value={option.value} className="bg-white dark:bg-[#0C0C10] text-gray-900 dark:text-white">
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <Label className="text-gray-500 dark:text-[#94A3B8] text-xs font-medium">Duration</Label>
                    <select
                      value={form.duration}
                      onChange={e => setForm(f => ({ ...f, duration: parseInt(e.target.value, 10) || 60 }))}
                      className="mt-1 w-full h-10 bg-gray-50 dark:bg-white/[0.03] border border-gray-200 dark:border-white/[0.08] rounded-md px-3 text-gray-900 dark:text-white text-sm font-plex focus:outline-none focus:border-[#00FF88]/50"
                      data-testid="class-duration-select"
                    >
                      {durationOptions.map(option => (
                        <option key={option} value={option} className="bg-white dark:bg-[#0C0C10] text-gray-900 dark:text-white">
                          {option} mins
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                {form.source === "manual" && (
                  <div>
                    <div className="flex items-center justify-between">
                      <Label className="text-gray-500 dark:text-[#94A3B8] text-xs font-medium">Meeting Link (Zoom / Meet / Live URL)</Label>
                      <span className="text-[10px] text-[#00FF88] font-mono">Any video link works</span>
                    </div>
                    <Input
                      value={form.meeting_link}
                      onChange={e => setForm(f => ({ ...f, meeting_link: e.target.value }))}
                      placeholder="https://zoom.us/j/... or https://meet.google.com/..."
                      className="mt-1 bg-gray-50 dark:bg-white/[0.03] border-gray-200 dark:border-white/[0.08] text-gray-900 dark:text-white font-plex"
                      required
                      data-testid="class-link-input"
                    />
                  </div>
                )}

                <div className="pt-2">
                  <Button type="submit" disabled={creating}
                    style={{ backgroundColor: "#00FF88", color: "#000" }}
                    className="w-full font-bold rounded-sm h-10 border-0 flex items-center justify-center gap-2 hover:opacity-90" data-testid="submit-class-btn">
                    {creating ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        <span>Creating Session...</span>
                      </>
                    ) : form.source === "zoom" ? (
                      <>
                        <Video className="w-4 h-4" />
                        <span>Create Automated Zoom Meeting</span>
                      </>
                    ) : (
                      <>
                        <CheckCircle className="w-4 h-4" />
                        <span>Publish Live Class</span>
                      </>
                    )}
                  </Button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Zoom Settings Modal (Admin Only) */}
        {showZoomSettings && (
          <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
            <div className="terminal-card rounded-xl p-6 w-full max-w-lg border border-gray-200 dark:border-white/10 shadow-2xl space-y-4">
              <div className="flex items-center justify-between border-b border-gray-200 dark:border-white/[0.08] pb-3">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-lg bg-[#2D8CFF]/10 flex items-center justify-center text-[#2D8CFF]">
                    <Video className="w-4 h-4" />
                  </div>
                  <div>
                    <h2 className="text-base font-bold text-gray-900 dark:text-white font-outfit">Zoom API Integration</h2>
                    <p className="text-[11px] text-gray-500 dark:text-[#94A3B8]">Server-to-Server OAuth Credentials</p>
                  </div>
                </div>
                <button onClick={() => setShowZoomSettings(false)} className="text-gray-400 hover:text-white"><X className="w-5 h-5" /></button>
              </div>

              {/* Status Banner */}
              <div className={`p-3 rounded-lg text-xs flex items-center justify-between ${zoomStatus.configured ? "bg-[#00FF88]/10 text-[#00FF88] border border-[#00FF88]/20" : "bg-amber-500/10 text-amber-300 border border-amber-500/20"}`}>
                <div className="flex items-center gap-2">
                  {zoomStatus.configured ? <ShieldCheck className="w-4 h-4" /> : <AlertTriangle className="w-4 h-4" />}
                  <span>{zoomStatus.configured ? "Zoom API is connected & verified" : "Zoom API not connected"}</span>
                </div>
                <span className="font-mono text-[10px]">{zoomStatus.configured ? "ACTIVE" : "INACTIVE"}</span>
              </div>

              {zoomMsg.text && (
                <div className={`p-3 rounded-lg text-xs ${zoomMsg.type === "success" ? "bg-[#00FF88]/10 text-[#00FF88] border border-[#00FF88]/20" : "bg-red-500/10 text-red-400 border border-red-500/20"}`}>
                  {zoomMsg.text}
                </div>
              )}

              <form onSubmit={saveZoomConfig} className="space-y-3">
                <div>
                  <Label className="text-xs text-gray-400">Account ID</Label>
                  <Input
                    value={zoomConfig.account_id}
                    onChange={e => setZoomConfig(c => ({ ...c, account_id: e.target.value }))}
                    placeholder="e.g., abcd1234efgh"
                    className="mt-1 font-mono text-xs bg-gray-50 dark:bg-white/[0.03] border-gray-200 dark:border-white/[0.08]"
                    required
                  />
                </div>
                <div>
                  <Label className="text-xs text-gray-400">Client ID</Label>
                  <Input
                    value={zoomConfig.client_id}
                    onChange={e => setZoomConfig(c => ({ ...c, client_id: e.target.value }))}
                    placeholder="e.g., your_client_id"
                    className="mt-1 font-mono text-xs bg-gray-50 dark:bg-white/[0.03] border-gray-200 dark:border-white/[0.08]"
                    required
                  />
                </div>
                <div>
                  <Label className="text-xs text-gray-400">Client Secret</Label>
                  <Input
                    type="password"
                    value={zoomConfig.client_secret}
                    onChange={e => setZoomConfig(c => ({ ...c, client_secret: e.target.value }))}
                    placeholder={zoomStatus.configured ? "•••••••••••• (leave blank to keep current)" : "Paste Zoom Client Secret"}
                    className="mt-1 font-mono text-xs bg-gray-50 dark:bg-white/[0.03] border-gray-200 dark:border-white/[0.08]"
                    required={!zoomStatus.configured}
                  />
                </div>
                <div>
                  <Label className="text-xs text-gray-400">Host User ID / Email (Optional)</Label>
                  <Input
                    value={zoomConfig.host_user_id}
                    onChange={e => setZoomConfig(c => ({ ...c, host_user_id: e.target.value }))}
                    placeholder="me or your zoom account email"
                    className="mt-1 text-xs bg-gray-50 dark:bg-white/[0.03] border-gray-200 dark:border-white/[0.08]"
                  />
                </div>

                {/* Quick Help Guide */}
                <div className="p-3 rounded-lg bg-gray-100 dark:bg-white/[0.02] border border-gray-200 dark:border-white/[0.06] text-[11px] text-gray-400 space-y-1">
                  <p className="font-semibold text-gray-300 flex items-center gap-1">
                    <HelpCircle className="w-3.5 h-3.5 text-[#00F0FF]" /> How to obtain Zoom credentials:
                  </p>
                  <ol className="list-decimal pl-4 space-y-0.5 text-gray-400">
                    <li>Log into <a href="https://marketplace.zoom.us/" target="_blank" rel="noreferrer" className="text-[#00F0FF] underline">marketplace.zoom.us</a></li>
                    <li>Click <strong>Develop &gt; Build App &gt; Server-to-Server OAuth</strong></li>
                    <li>Add Scopes: <code className="text-xs text-[#00FF88]">meeting:write:admin</code> & <code className="text-xs text-[#00FF88]">meeting:read:admin</code></li>
                    <li>Copy Account ID, Client ID, and Client Secret and paste here.</li>
                  </ol>
                </div>

                <div className="flex gap-2 pt-2">
                  <Button type="button" variant="outline" onClick={() => setShowZoomSettings(false)} className="flex-1 text-xs">
                    Cancel
                  </Button>
                  <Button type="submit" disabled={savingZoom} className="flex-1 text-xs bg-[#00FF88] text-black font-bold hover:bg-[#00FF88]/90">
                    {savingZoom ? <Loader2 className="w-4 h-4 animate-spin" /> : "Verify & Save Credentials"}
                  </Button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Classes List */}
        {classes.length === 0 ? (
          <div className="terminal-card rounded-lg p-12 text-center">
            <Video className="w-10 h-10 text-gray-400 dark:text-[#52525B] mx-auto mb-3" />
            <p className="text-gray-500 dark:text-[#94A3B8] font-plex">No live classes scheduled yet</p>
            {isStaff && <p className="text-xs text-gray-400 dark:text-[#52525B] font-plex mt-1">Click "Schedule Class" to create your first session</p>}
          </div>
        ) : (
          <div className="space-y-3" data-testid="classes-list">
            {classes.map(cls => {
              const st = statusConfig[cls.status] || statusConfig.upcoming;
              const StatusIcon = st.icon;
              const schedDate = cls.scheduled_at ? new Date(cls.scheduled_at) : null;

              return (
                <div key={cls.class_id} className="terminal-card rounded-lg overflow-hidden group hover:border-gray-200 dark:border-white/10 transition-colors"
                  style={{ borderLeft: `3px solid ${st.color}` }} data-testid={`class-${cls.class_id}`}>
                  <div className="p-5">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-2">
                          <Badge style={{ backgroundColor: st.bg, color: st.color }} className="border-0 text-[10px] font-jetbrains flex items-center gap-1">
                            <StatusIcon className="w-3 h-3" />{st.label}
                          </Badge>
                          {cls.source === "zoom" && <Badge className="bg-[#2D8CFF]/10 text-[#2D8CFF] border-0 text-[9px] font-jetbrains">Zoom</Badge>}
                          {cls.source === "manual" && <Badge className="bg-gray-100 dark:bg-white/5 text-gray-500 dark:text-[#94A3B8] border-0 text-[9px] font-jetbrains">External</Badge>}
                          <Badge className="bg-[#00FF88]/10 text-[#00FF88] border-0 text-[9px] font-jetbrains">
                            <Users className="w-3 h-3 mr-1" />{cls.course_title || "All Students"}
                          </Badge>
                        </div>
                        <h3 className="text-base font-bold text-gray-900 dark:text-white font-outfit mb-1">{cls.title}</h3>
                        {cls.description && <p className="text-xs text-gray-500 dark:text-[#94A3B8] font-plex mb-3 line-clamp-2">{cls.description}</p>}
                        <div className="flex items-center gap-4 text-[11px] font-jetbrains text-gray-500 dark:text-[#94A3B8]">
                          {schedDate && (
                            <span className="flex items-center gap-1">
                              <Calendar className="w-3.5 h-3.5" />
                              {schedDate.toLocaleDateString("en-IN", { weekday: "short", day: "numeric", month: "short", year: "numeric" })}
                            </span>
                          )}
                          {schedDate && (
                            <span className="flex items-center gap-1">
                              <Clock className="w-3.5 h-3.5" />
                              {schedDate.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" })}
                            </span>
                          )}
                          <span className="flex items-center gap-1">
                            <Play className="w-3.5 h-3.5" />{cls.duration} min
                          </span>
                        </div>
                      </div>
                      <div className="flex flex-col gap-2 shrink-0">
                        {cls.status === "live" && cls.join_url ? (
                          <a href={cls.join_url} target="_blank" rel="noopener noreferrer">
                            <Button size="sm"
                              style={{ backgroundColor: "#00FF88", color: "#000" }}
                              className="font-bold rounded-sm text-xs h-8 px-4 border-0 flex items-center gap-1.5 animate-pulse shadow-md shadow-emerald-500/20" data-testid={`join-${cls.class_id}`}>
                              <Radio className="w-3.5 h-3.5" /> Join Live Now
                            </Button>
                          </a>
                        ) : cls.status === "upcoming" ? (
                          isStaff && (cls.join_url || cls.start_url) ? (
                            <a href={cls.join_url || cls.start_url} target="_blank" rel="noopener noreferrer">
                              <Button size="sm"
                                variant="outline"
                                className="font-semibold rounded-sm text-xs h-8 px-3 border-cyan-500/40 text-cyan-600 dark:text-cyan-400 hover:bg-cyan-500/10 flex items-center gap-1">
                                <ExternalLink className="w-3 h-3" /> Preview (Staff)
                              </Button>
                            </a>
                          ) : (
                            <Button size="sm"
                              variant="outline"
                              disabled
                              className="font-medium rounded-sm text-xs h-8 px-3 border-gray-300 dark:border-white/10 text-gray-500 dark:text-zinc-400 bg-gray-100 dark:bg-white/[0.03] flex items-center gap-1.5 cursor-not-allowed opacity-90">
                              <Lock className="w-3.5 h-3.5 text-amber-500" />
                              <span>Opens at {schedDate ? schedDate.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" }) : "Scheduled Time"}</span>
                            </Button>
                          )
                        ) : null}
                        {isStaff && cls.start_url && cls.status !== "completed" && (
                          <a href={cls.start_url} target="_blank" rel="noopener noreferrer">
                            <Button size="sm" variant="ghost" className="text-[#FFCC00] hover:bg-[#FFCC00]/10 rounded-sm text-[10px] h-7 px-3 w-full font-bold">
                              Start as Host
                            </Button>
                          </a>
                        )}
                        {isStaff && (
                          <Button size="sm" variant="ghost" onClick={() => deleteClass(cls.class_id)}
                            className="text-[#FF3B30] hover:bg-[#FF3B30]/10 rounded-sm text-[10px] h-6 px-2" data-testid={`delete-${cls.class_id}`}>
                            Delete
                          </Button>
                        )}
                      </div>
                    </div>
                    {cls.passcode && isStaff && (
                      <div className="mt-3 pt-2 border-t border-gray-100 dark:border-white/[0.04] text-[10px] font-jetbrains text-gray-400 dark:text-[#52525B]">
                        Passcode: <span className="text-[#FFCC00]">{cls.passcode}</span>
                        {cls.zoom_meeting_id && <span className="ml-4">Meeting ID: {cls.zoom_meeting_id}</span>}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default LiveClassesPage;
